/**
 * Wraps a Promise-returning function so it can be used where a void-returning
 * callback is expected (e.g., React event handlers, third-party APIs typed as
 * `() => void`).
 *
 * The Promise is explicitly discarded with `void`, which:
 *   - Tells TypeScript and ESLint the discard is intentional
 *   - Allows unhandled rejections to surface to `window.onunhandledrejection`
 *     (wired to Sentry) instead of being silently swallowed
 *   - Preserves the wrapped function's parameter list so the caller's args
 *     still flow through
 *
 * Usage:
 *   <button onClick={fireAndForget(handleSave)}>Save</button>
 *   <form onSubmit={fireAndForget(async (e) => { e.preventDefault(); await save(); })}>
 */
// `fn` is typed `=> unknown` rather than `=> Promise<unknown>` deliberately.
// Real call sites pass conditional expressions whose type is a union like
// `Promise<void> | undefined | ""` (e.g. `() => id && fetchThing(id)`), which
// a `Promise`-only signature would reject. Type-narrowing the input here would
// just push `as`-casts to every call site. The misuse this guards against
// (passing a Promise where void is expected) is already caught by the
// `@typescript-eslint/no-misused-promises` ERROR rule at the *original*
// unwrapped call site — wrapping with fireAndForget is the sanctioned fix, so
// the permissive input type is intentional, not a hole.
export function fireAndForget<TArgs extends unknown[]>(
  fn: (...args: TArgs) => unknown,
): (...args: TArgs) => void {
  return (...args) => {
    void fn(...args);
  };
}
